import React from 'react';
import {
  Box,
  Typography,
  Chip,
  Button,
} from '@mui/material';
import {
  TrendingUp,
  CheckCircle,
  SignalCellularAlt,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { SnapbackTableSkeleton } from './ReversalCatcherSkeleton';

const SnapbackList = ({ stocks, onRefresh, loading = false }) => {
  const { t } = useTranslation();

  if (loading && (!stocks || stocks.length === 0)) {
    return <SnapbackTableSkeleton rows={6} />;
  }
  
  if (!stocks || stocks.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 6 }}>
        <TrendingUp sx={{ fontSize: 48, color: '#cbd5e1', mb: 1.5 }} />
        <Typography sx={{ fontSize: '0.95rem', fontWeight: 600, color: '#64748b', mb: 0.5 }}>
          {t('reversalCatcher.snapback.emptyTitle')}
        </Typography>
        <Typography sx={{ fontSize: '0.78rem', color: '#94a3b8' }}>
          {t('reversalCatcher.snapback.emptyDesc')}
        </Typography>
      </Box>
    );
  }
  
  return (
    <Box>
      {/* Summary */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.85 }}>
        <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#475569' }}>
          {t('reversalCatcher.snapback.foundTitle', { count: stocks.length })}
        </Typography>
        <Typography sx={{ fontSize: '0.68rem', color: '#94a3b8' }}>
          {t('reversalCatcher.snapback.foundHint')}
        </Typography>
      </Box>
      
      {/* Table */}
      <Box sx={{ 
        background: '#ffffff', 
        border: '1px solid #e5e7eb', 
        borderRadius: 2, 
        overflow: 'hidden' 
      }}>
        {/* Header */}
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: '0.9fr 0.7fr 0.9fr 1.2fr 0.8fr 0.8fr 0.9fr 0.9fr 0.7fr 1fr',
          p: '8px 12px',
          background: 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)',
          borderBottom: '1px solid #e2e8f0'
        }}>
          {[
            t('common.ticker'),
            t('reversalCatcher.snapback.score'),
            t('reversalCatcher.snapback.price'),
            t('reversalCatcher.snapback.entry'),
            t('reversalCatcher.snapback.stop'),
            t('reversalCatcher.snapback.target'),
            t('reversalCatcher.snapback.riskRr'),
            t('reversalCatcher.snapback.expectancy'),
            t('reversalCatcher.snapback.abc'),
            t('common.action'),
          ].map((h, i) => (
            <Typography key={i} sx={{ 
              fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', 
              letterSpacing: '0.3px', color: '#64748b',
              textAlign: [2,3,4,5].includes(i) ? 'right' : [1,6,7,8,9].includes(i) ? 'center' : 'left'
            }}>
              {h}
            </Typography>
          ))}
        </Box>
        
        {/* Rows */}
        {stocks.map((stock, idx) => {
          const isAdd = stock.state === 'ADD_SNAP';
          const borderColor = isAdd ? '#22c55e' : '#3b82f6';
          
          return (
            <Box key={stock.ticker} sx={{ 
              display: 'grid', 
              gridTemplateColumns: '0.9fr 0.7fr 0.9fr 1.2fr 0.8fr 0.8fr 0.9fr 0.9fr 0.7fr 1fr',
              p: '7px 12px',
              alignItems: 'center',
              borderBottom: '1px solid #f8fafc',
              borderLeft: `3px solid ${borderColor}`,
              transition: 'background 0.15s',
              '&:hover': { background: isAdd ? '#f0fdf4' : '#eff6ff' },
              ...(idx % 2 === 1 ? { bgcolor: '#fafbfc' } : {})
            }}>
              {/* Ticker */}
              <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#1e293b' }}>
                {stock.ticker}
              </Typography>
              
              {/* Score */}
              <Typography sx={{ fontSize: '0.82rem', fontWeight: 800, color: '#667eea', textAlign: 'center', fontFamily: 'monospace' }}>
                {stock.total_score?.toFixed(1)}
              </Typography>
              
              {/* Price */}
              <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: '#1e293b', textAlign: 'right', fontFamily: 'monospace' }}>
                {stock.current_price?.toLocaleString()}
              </Typography>
              
              {/* Entry Range */}
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#667eea', textAlign: 'right', fontFamily: 'monospace' }}>
                {stock.entry_low && stock.entry_high
                  ? `${stock.entry_low.toLocaleString()} – ${stock.entry_high.toLocaleString()}`
                  : '—'}
              </Typography>
              
              {/* Stop */}
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#dc2626', textAlign: 'right', fontFamily: 'monospace' }}>
                {stock.stop ? stock.stop.toLocaleString() : '—'}
              </Typography>
              
              {/* Target */}
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#059669', textAlign: 'right', fontFamily: 'monospace' }}>
                {stock.target1 ? stock.target1.toLocaleString() : '—'}
              </Typography>
              
              {/* Risk / R:R */}
              <Box sx={{ textAlign: 'center' }}>
                {stock.risk_pct != null && (
                  <Typography sx={{ fontSize: '0.68rem', color: '#dc2626', fontWeight: 600, lineHeight: 1.2 }}>
                    {t('reversalCatcher.snapback.riskPct', { value: (stock.risk_pct * 100).toFixed(1) })}
                  </Typography>
                )}
                {stock.rr != null && (
                  <Typography sx={{ fontSize: '0.68rem', color: '#059669', fontWeight: 700, lineHeight: 1.2 }}>
                    {t('reversalCatcher.snapback.rrRatio', { value: stock.rr.toFixed(1) })}
                  </Typography>
                )}
              </Box>
              
              {/* Expectancy */}
              <Box sx={{ textAlign: 'center' }}>
                {stock.p_win != null && (
                  <Typography sx={{ fontSize: '0.62rem', color: '#94a3b8', lineHeight: 1.3 }}>
                    {t('reversalCatcher.snapback.probWin', { value: (stock.p_win * 100).toFixed(0) })}
                  </Typography>
                )}
                {stock.expectancy_pct != null && (
                  <Chip
                    label={`${stock.expectancy_pct > 0 ? '+' : ''}${stock.expectancy_pct.toFixed(1)}%`}
                    size="small"
                    sx={{
                      height: 16, fontSize: '0.58rem', fontWeight: 700, borderRadius: 1,
                      bgcolor: stock.expectancy_pct > 2 ? '#059669' : stock.expectancy_pct > 0 ? '#3b82f6' : '#dc2626',
                      color: 'white',
                    }}
                  />
                )}
              </Box>
              
              {/* ABC */}
              <Typography sx={{ 
                fontSize: '0.68rem', fontWeight: 600, textAlign: 'center', fontFamily: 'monospace',
                color: stock.all_filters_ok ? '#059669' : '#94a3b8'
              }}>
                {stock.all_filters_ok && '✓'}{stock.a_score}/{stock.b_score}/{stock.c_score}
              </Typography>
              
              {/* Action */}
              <Box sx={{ textAlign: 'center' }}>
                {isAdd ? (
                  <Button 
                    variant="contained" 
                    startIcon={<CheckCircle sx={{ fontSize: 14 }} />}
                    size="small"
                    sx={{ 
                      fontWeight: 700, fontSize: '0.68rem', py: 0.3, px: 1.5, minHeight: 26, 
                      textTransform: 'none', borderRadius: 1.5,
                      bgcolor: '#22c55e', '&:hover': { bgcolor: '#16a34a' }
                    }}
                  >
                    {t('reversalCatcher.snapback.buyStrong')}
                  </Button>
                ) : (
                  <Button 
                    variant="outlined" 
                    startIcon={<SignalCellularAlt sx={{ fontSize: 14 }} />}
                    size="small"
                    sx={{ 
                      fontWeight: 700, fontSize: '0.68rem', py: 0.3, px: 1.5, minHeight: 26,
                      textTransform: 'none', borderRadius: 1.5,
                      borderColor: '#3b82f6', color: '#3b82f6',
                      '&:hover': { bgcolor: '#eff6ff', borderColor: '#2563eb' }
                    }}
                  >
                    {t('reversalCatcher.snapback.buyTest')}
                  </Button>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default SnapbackList;
